import { action, makeObservable, observable } from 'mobx';
import { getAppContext } from '~/lib/AppContext';
import { HomeRoute, isWithinSection, SupportRoute } from '~/lib/helper/RouteHelper';
import { field, is, validate } from '~/lib/helper/ValidationHelper';
import FormStore from '~/lib/store/FormStore';

export interface LoginFormValues {
    email: string;
    password: string;
    code: string;
    noExpire: boolean;
}

export function emptyFormValues(): LoginFormValues {
    return {
        email: '',
        password: '',
        code: '',
        noExpire: false,
    };
}

export class LoginStore {
    loginForm = new FormStore<LoginFormValues>(emptyFormValues());

    @observable
    codeInput: 'none' | 'email' | 'authenticator' = 'none';

    @observable
    showSendSMS = false;

    @observable
    hasFailedLogin = false;

    /** Whether /logout just logged the customer out. */
    @observable
    showLoggedOut = false;

    @observable
    verificationAlert:
        | {
              message: string;
              type: 'success' | 'danger' | 'warning';
          }
        | undefined = undefined;

    redirectUrl?: string;

    constructor(redirectUrl?: string) {
        makeObservable(this);
        this.redirectUrl = redirectUrl;
        this.init();
    }

    @action
    init() {
        this.codeInput = 'none';
        this.showSendSMS = false;
        this.hasFailedLogin = false;
        this.verificationAlert = undefined;
        this.loginForm.init(emptyFormValues());
    }

    @action
    setShowLoggedOut(showLoggedOut: boolean) {
        this.showLoggedOut = showLoggedOut;
    }

    @action
    private setVerificationAlert(message: string, type: 'success' | 'danger' | 'warning') {
        this.verificationAlert = {
            message,
            type,
        };
    }

    @action
    private clearAlerts() {
        this.showLoggedOut = false;
        this.verificationAlert = undefined;
        this.showSendSMS = false;
        this.hasFailedLogin = false;
    }

    @action
    private markLoginFailed(errors: { email?: string[]; password?: string[] }) {
        this.loginForm.setValidationErrors(errors);
        this.hasFailedLogin = true;
    }

    @action
    private enterVerificationStep(result: 'one-time-authenticator-password' | 'one-time-email-password') {
        const wasAlreadyOnVerfiyPage = this.codeInput !== 'none';

        if (result === 'one-time-authenticator-password') {
            this.codeInput = 'authenticator';
            this.setVerificationAlert('Verification code from your authenticator required', 'warning');
        } else {
            this.codeInput = 'email';
            this.setVerificationAlert(
                'We sent a verification code to your email. Enter the code from the email in the field below. The verification code will expire in 5 minutes.',
                'warning'
            );
        }

        if (wasAlreadyOnVerfiyPage) {
            this.loginForm.setValidationErrors({
                code: ['Invalid verification code'],
            });
        }
    }

    async doLogin() {
        if (
            !this.loginForm.validate((e) =>
                validate(
                    e,
                    field('email', is.required('Email is required'), is.minLength(1, 'Email is required')),
                    field('password', is.required('Password is required'), is.minLength(1, 'Password is required'))
                )
            )
        ) {
            return;
        }

        const loginRequest = {
            email: this.loginForm.values.email,
            password: this.loginForm.values.password,
            code: this.loginForm.values.code,
            no_expire: this.loginForm.values.noExpire,
        };

        this.clearAlerts();

        const loginResult = await getAppContext().siteApiClient.login(loginRequest);

        if (loginResult.result === 'success') {
            const returnTo = this.validatedRedirectUrl();

            // The support hand-off (/support/*-sso) is itself just an onward redirect to the
            // external support portal, so honour it even for customers who are otherwise sent
            // to ClassicPanel — otherwise a cold ticket-link arrival never reaches the portal.
            // Scope to the /support SPA section exactly, so an unrelated /support… route can't slip through.
            if (returnTo && isWithinSection(returnTo.pathname, SupportRoute)) {
                return getAppContext().navigate(returnTo.toString());
            }

            // Check panel migration status and redirect to classic panel if needed
            try {
                const migrationStatus = await getAppContext().siteApiClient.getPanelMigrationStatus();
                if (
                    migrationStatus.migration_status === 'monorail' ||
                    migrationStatus.migration_status === 'monorail-preferred' ||
                    migrationStatus.migration_status === 'react-available' ||
                    migrationStatus.migration_status === 'react-invite'
                ) {
                    window.location.href = `${getAppContext().config.wwwSite}/mpanel`;
                    return;
                }
            } catch (error) {
                // If we can't fetch migration status, continue with normal flow
                console.warn('Failed to fetch panel migration status:', error);
            }

            if (returnTo) {
                return getAppContext().navigate(returnTo.toString());
            }

            return getAppContext().navigate(HomeRoute);
        } else {
            switch (loginResult.result) {
                case 'blocked':
                    this.markLoginFailed({ email: ['Account is blocked'] });
                    break;
                case 'failure-count-exceeded':
                    this.markLoginFailed({ email: ['Too many failed login attempts'] });
                    break;
                case 'password-expired':
                    this.markLoginFailed({ password: ['Password expired, please reset'] });
                    break;
                case 'invalid-login':
                    this.markLoginFailed({ password: ['Invalid email or password'] });
                    break;
                case 'one-time-authenticator-password':
                case 'one-time-email-password': {
                    this.enterVerificationStep(loginResult.result);
                    break;
                }
            }
        }
    }

    /** The post-login `r` return URL, parsed and accepted only if same protocol + origin. */
    private validatedRedirectUrl(): URL | undefined {
        if (!this.redirectUrl) {
            return undefined;
        }
        try {
            const url = new URL(this.redirectUrl);
            if (url.protocol === window.location.protocol && url.origin === window.location.origin) {
                return url;
            }
        } catch {
            // Ignore a malformed redirect URL and fall through to the default destination.
        }
        return undefined;
    }

    async sendCodeViaSMS() {
        try {
            const request = {
                email: this.loginForm.values.email,
                password: this.loginForm.values.password,
            };

            const response = await getAppContext().siteApiClient.sendCodeViaSms(request);

            if (response === 'success') {
                this.setVerificationAlert('Verification code has been sent to your phone.', 'success');
            } else if (response === 'rate-limited') {
                this.setVerificationAlert('Please wait a minute before requesting another code.', 'warning');
            } else {
                this.setVerificationAlert('Unable to send code.', 'danger');
            }
        } catch (error) {
            this.setVerificationAlert('Unable to send code.', 'danger');
            throw error;
        }
    }

    async resendEmail() {
        try {
            const request = {
                email: this.loginForm.values.email,
                password: this.loginForm.values.password,
            };

            await getAppContext().siteApiClient.sendEmailCode(request);

            this.setVerificationAlert('Verification code has been sent to your email address.', 'success');
        } catch (error) {
            this.setVerificationAlert('Failed to send email code. Please try again.', 'danger');
            throw error;
        }
    }

    @action
    handleBackToLogin() {
        this.init();
    }

    @action
    handleShowSendSMS() {
        this.showSendSMS = true;
    }
}
